import { Component, ElementRef, ViewChild, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { PDFDocument, rgb } from 'pdf-lib';
// Third Party
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
// Functions
import {
  capitalizeFirst,
  capitalizeWordsLongerThan1,
  concatenateCompanyCityStateAddress,
  concatenateCompanyStreetAddress,
  concatenateCustomerCityStateAddress,
  concatenateCustomerStreetAddress,
  formatPhoneNumber,
} from 'src/app/shared/functions';
// Models
import { Customer } from 'src/app/shared/models/accounting';
import {
  ContactTypes,
  ModalID,
  ModalType,
  StatusModalIndicator,
} from 'src/app/shared/models/constants';
import { BoundBookEntry } from 'src/app/shared/models/general';
// Services
import { SharedService } from 'src/app/shared/services/shared.service';
import { AccountingService } from 'src/app/accounting/services/accounting.service';
import { getCustomerById } from 'src/app/accounting/shared/functions';
import { Subscription } from 'rxjs';
import { NgSelectModule } from '@ng-select/ng-select';
import { FormsModule } from '@angular/forms';
import { CapitalizeFirstPipe } from 'src/app/shared/pipes/capitalize-first.pipe';

@Component({
  selector: 'app-ffl-packing-slip-print',
  standalone: true,
  imports: [NgSelectModule, FormsModule, CapitalizeFirstPipe],
  templateUrl: './ffl-packing-slip-print.component.html',
  styleUrl: './ffl-packing-slip-print.component.scss',
})
export class FflPackingSlipPrintComponent implements OnInit {
  @ViewChild('shipping_slip') shipping_slip: ElementRef;
  subscriptions = new Subscription();
  jsPDF: any;
  company_street_address = '';
  company_city_state_zip = '';
  customer_info: Customer;
  customer_street_address = '';
  customer_city_state_zip = '';
  modalRef: BsModalRef;
  modalConfig = { animated: true };
  disposition_items: BoundBookEntry[] = [];
  selected_customer: Customer = null;
  selected_customer_street_address = '';
  selected_customer_city_state_zip = '';
  customer_placeholder: string = 'Select Customer';
  public formatPhoneNumber = formatPhoneNumber;
  notes_value = '';

  constructor(
    protected element: ElementRef,
    private modalService: BsModalService,
    public sharedService: SharedService,
    public accountingService: AccountingService,
    private sanitizer: DomSanitizer,
  ) {
    this.subscriptions.add(
      this.accountingService.customer_list_retrieved$.subscribe((customers) => {
        this.accountingService.customers = [
          ...this.accountingService.customers,
        ];
        console.warn(this.accountingService.customers);
      }),
    );
    // contact_to_add
    this.subscriptions.add(
      this.accountingService.customer_contact_to_add$.subscribe((response) => {
        this.addCustomer(response);
      }),
    );
    // contact_to_edited
    this.subscriptions.add(
      this.accountingService.customer_contact_to_edit$.subscribe(
        (edit_customer_response_package) => {
          this.editCustomer(edit_customer_response_package.customer);
        },
      ),
    );
    this.subscriptions.add(
      this.accountingService.customer_list_updated$.subscribe((customers) => {
        this.accountingService.customers = [
          ...this.accountingService.customers,
        ];
        this.selected_customer = this.accountingService.customers.find(
          (c) => c.id === this.selected_customer?.id,
        );
        this.getCustomerInfo();
      }),
    );
  }

  async ngOnInit() {
    console.warn(this.accountingService.customers);
    if (this.accountingService.customers.length === 0) {
      await this.accountingService.getCustomers();
    }
    this.company_street_address = concatenateCompanyStreetAddress(
      this.sharedService.company_info,
    );
    this.company_city_state_zip = concatenateCompanyCityStateAddress(
      this.sharedService.company_info,
    );

    if (this.disposition_items && this.disposition_items.length > 0) {
      const firstItem = this.disposition_items[0];

      // Initialize customer_info from the first item
      this.customer_info = new Customer();
      this.customer_info.contact.display_name =
        firstItem.d_disposed_to || firstItem.d_business_name;
      this.customer_info.address.street = firstItem.d_address;
      this.customer_info.address.street2 = firstItem.d_address2;
      this.customer_info.address.city = firstItem.d_city;
      this.customer_info.address.state = firstItem.d_state;
      this.customer_info.address.zip_code = firstItem.d_zip_code?.toString();

      // Concatenate addresses
      this.customer_street_address = concatenateCustomerStreetAddress(
        this.customer_info,
      );
      this.customer_city_state_zip = concatenateCustomerCityStateAddress(
        this.customer_info,
      );
    }

    // Fetch FFL if missing
    if (!this.sharedService.company_info.ffl_document && this.sharedService.company_info.company_id) {
      const result = await this.sharedService.getFFLDocument(this.sharedService.company_info.company_id);
      this.sharedService.company_info.ffl_document = result.document;
    }
  }

  closeModalClick(): void {
    this.modalService.hide(ModalID.ffl_packing_slip_print);
  }

  async printPackingSlipClick() {
    const DATA = this.shipping_slip.nativeElement;

    // 1. Prepare for Capture (Force Tight Layout)
    const originalWidth = DATA.style.width;
    const originalHeight = DATA.style.height;
    const originalMinHeight = DATA.style.minHeight;
    const originalPaddingBottom = DATA.style.paddingBottom;
    const originalOverflow = DATA.style.overflow;
    const originalBoxShadow = DATA.style.boxShadow;
    const originalMargin = DATA.style.margin;

    DATA.style.width = '1015px';
    DATA.style.height = 'auto';
    DATA.style.minHeight = '0'; // Crucial to prevent empty gaps at the bottom
    DATA.style.paddingBottom = '0';
    DATA.style.overflow = 'visible';
    DATA.style.boxShadow = 'none';
    DATA.style.margin = '0';

    // Mirror Notes (Ensures text values are captured cleanly)
    const notesTextarea = DATA.querySelector('#notes') as HTMLTextAreaElement;
    let notesMirror: HTMLDivElement | null = null;
    if (notesTextarea) {
      notesMirror = document.createElement('div');
      notesMirror.innerText = notesTextarea.value || ' ';
      notesMirror.style.cssText = `
        display: block;
        width: 100%;
        min-height: 20px;
        padding: 0;
        font-family: inherit;
        font-size: 14px;
        border: none;
        border-radius: 0;
        white-space: pre-wrap;
        margin-top: 5px;
        margin-bottom: 20px;
        background-color: transparent;
      `;
      notesTextarea.parentNode?.insertBefore(notesMirror, notesTextarea);
      notesTextarea.style.display = 'none';
    }

    // Hide Ignored elements
    const ignoredElements = DATA.querySelectorAll('[data-html2canvas-ignore]');
    const originalStyles = new Map();
    ignoredElements.forEach((el: any) => {
      originalStyles.set(el, el.style.display);
      el.style.display = 'none';
    });

    // Measure true content height accurately after hiding ignored items
    const rect = DATA.getBoundingClientRect();
    const contentHeight = Math.ceil(rect.height);

    try {
      // 2. Capture Content (Tight Crop)
      const canvas = await html2canvas(DATA, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: 1015,
        height: contentHeight,
      });
      const imgData = canvas.toDataURL('image/jpeg', 0.95);

      // Restore UI immediately
      ignoredElements.forEach(
        (el: any) => (el.style.display = originalStyles.get(el)),
      );
      if (notesTextarea && notesMirror) {
        notesMirror.remove();
        notesTextarea.style.display = 'block';
      }
      DATA.style.width = originalWidth;
      DATA.style.height = originalHeight;
      DATA.style.minHeight = originalMinHeight;
      DATA.style.paddingBottom = originalPaddingBottom;
      DATA.style.overflow = originalOverflow;
      DATA.style.boxShadow = originalBoxShadow;
      DATA.style.margin = originalMargin;

      // 3. Create PDF and Embed Content
      const pdfDoc = await PDFDocument.create();
      const page = pdfDoc.addPage([595.28, 841.89]); // A4
      const { width: pageWidth, height: pageHeight } = page.getSize();

      // Load FFL PDF and crop to the top half
      let fflPageEmbed: any = null;
      let fflAspect = 0;
      const fflBase64 = this.sharedService.company_info.ffl_document;

      if (fflBase64) {
        try {
          const base64Data = fflBase64.split(',')[1] || fflBase64;
          const fflDocBody = await PDFDocument.load(base64Data);
          const fflPage = fflDocBody.getPage(0);
          const { width: fw, height: fh } = fflPage.getSize();

          [fflPageEmbed] = await pdfDoc.embedPdf(fflDocBody, [0]);

          // Use the full aspect ratio since we'll draw the full page and mask the bottom
          fflAspect = fh / fw;
        } catch (e) {
          console.error('Error preparing FFL PDF layer:', e);
        }
      }

      // 4. Calculate Scaling and Layout
      const htmlImage = await pdfDoc.embedJpg(imgData);
      const htmlAspect = htmlImage.height / htmlImage.width;

      const margin = 35; // Slightly smaller margins for more workspace
      const availableWidth = pageWidth - margin * 2;
      const availableHeight = pageHeight - margin * 2;

      let slipWidth = availableWidth;
      let slipHeight = slipWidth * htmlAspect;

      // Increase FFL width to look more like the "should look like" sample (e.g., 85% width)
      let fflWidth = availableWidth * 0.5;
      let fflHeightFull = fflPageEmbed ? fflWidth * fflAspect : 0;
      let fflHeightVisible = fflHeightFull / 2;

      const gap = 10; // Tight gap for professional look
      let totalDocHeight =
        slipHeight + (fflPageEmbed ? fflHeightVisible + gap : 0);

      // Proportionally scale both if they exceed the page
      if (totalDocHeight > availableHeight) {
        const factor = availableHeight / totalDocHeight;
        slipWidth *= factor;
        slipHeight *= factor;
        fflWidth *= factor;
        fflHeightFull *= factor;
        fflHeightVisible *= factor;
      }

      // 5. Drawing (Stacked Top-Down)
      let currentY = pageHeight - margin;

      // Draw Packing Slip
      currentY -= slipHeight;
      page.drawImage(htmlImage, {
        x: (pageWidth - slipWidth) / 2,
        y: currentY,
        width: slipWidth,
        height: slipHeight,
      });

      // Draw FFL immediately below the slip with a white mask to crop the bottom half
      if (fflPageEmbed) {
        currentY -= fflHeightVisible + gap;

        // Draw the FULL FFL page
        page.drawPage(fflPageEmbed, {
          x: margin,
          y: currentY - fflHeightVisible, // Shift it so bottom half is below the "visible" area
          width: fflWidth,
          height: fflHeightFull,
        });

        // Mask the bottom half with a white rectangle to effectively crop it
        page.drawRectangle({
          x: margin,
          y: currentY - fflHeightVisible,
          width: fflWidth,
          height: fflHeightVisible,
          color: rgb(1, 1, 1), // Pure White
        });
      }

      // 6. Finalize and Download
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes as any], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Packing-Slip-${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Final PDF Generation Error:', error);
      ignoredElements.forEach(
        (el: any) => (el.style.display = originalStyles.get(el)),
      );
      if (notesTextarea && notesMirror) {
        notesMirror.remove();
        notesTextarea.style.display = 'block';
      }
      DATA.style.width = originalWidth;
      DATA.style.height = originalHeight;
      DATA.style.minHeight = originalMinHeight;
      DATA.style.paddingBottom = originalPaddingBottom;
      DATA.style.overflow = originalOverflow;
      DATA.style.boxShadow = originalBoxShadow;
      DATA.style.margin = originalMargin;
    }
    this.closeModalClick();
  }

  getCustomerInfo() {
    if (this.selected_customer) {
      this.selected_customer_street_address = concatenateCustomerStreetAddress(
        this.selected_customer,
      );
      this.selected_customer_city_state_zip =
        concatenateCustomerCityStateAddress(this.selected_customer);
      this.getCustomerAddress();
      this.getCustomerEmail();
    }
  }
  getCustomerAddress() {
    const c = this.selected_customer;
    if (!c) return;
    let billing_address: string = '';

    const name_first = c.contact.name_first ? c.contact.name_first : '';
    const name_last = c.contact.name_last ? c.contact.name_last : '';
    const street = c.address.street ? c.address.street : '';
    const street2 = c.address.street2 ? c.address.street2 : '';
    const city = c.address.city ? c.address.city : '';
    const state = c.address.state ? c.address.state : '';
    const zip_code = c.address.zip_code ? c.address.zip_code : '';

    billing_address =
      `${capitalizeFirst(name_first)} ${capitalizeFirst(name_last)}`.trim() +
      (street ? `\n${capitalizeWordsLongerThan1(street)}` : '') +
      (street2 ? ` ${capitalizeWordsLongerThan1(street2)}` : '') +
      (capitalizeFirst(city) || state || zip_code
        ? `\n${[city, state, zip_code]
            .filter(Boolean)
            .join(', ')
            .replace(/, $/, '')}`
        : '');
  }
  getCustomerEmail() {}
  customerDynamicPlaceholder() {
    if (!!this.selected_customer) {
      this.customer_placeholder = 'Select Customer';
    } else {
      this.customer_placeholder = '';
    }
  }
  clearCustomerFields() {
    this.selected_customer = null;
    this.selected_customer_street_address = '';
    this.selected_customer_city_state_zip = '';
  }
  clearCustomerAddress() {
    // this.salesForm.patchValue({
    //   billing_address: '',
    // });
  }
  clearCustomerEmail() {
    // this.salesForm.patchValue({
    //   email: '',
    // });
  }
  async addCustomer(customer: Customer) {
    this.sharedService.openStatusSpinner('Adding Customer');
    await this.accountingService.addNewCustomer(customer);
    this.sharedService.closeStatusSpinner();
  }
  addNewCustomerClick() {
    this.sharedService.openAddEditContactModal(
      ModalType.add,
      ContactTypes.customer,
    );
  }
  editCustomer(customer: Customer) {
    this.accountingService.editCustomer(customer);
  }
  editCustomerClick() {
    this.sharedService.openAddEditContactModal(
      ModalType.edit,
      ContactTypes.customer,
      this.selected_customer,
    );
  }
  getSanitizedFflUrl(): SafeResourceUrl | null {
    const raw = this.sharedService.company_info?.ffl_document;
    if (!raw || typeof raw !== 'string') {
      return null;
    }
    try {
      const parsed = new URL(raw, window.location.origin);
      const isHttps = parsed.protocol === 'https:';
      const isBlob = parsed.protocol === 'blob:';
      const isPdfData =
        parsed.protocol === 'data:' &&
        raw.toLowerCase().startsWith('data:application/pdf');
      if (!isHttps && !isBlob && !isPdfData) {
        return null;
      }
      const allowedHosts = new Set([
        window.location.hostname,
        'www.fflxcel.net',
        'fflxcel.net',
      ]);
      if (isHttps && !allowedHosts.has(parsed.hostname)) {
        return null;
      }
      parsed.hash = 'toolbar=0&navpanes=0&scrollbar=0&view=FitH&zoom=100';
      return this.sanitizer.bypassSecurityTrustResourceUrl(parsed.toString());
    } catch {
      return null;
    }
  }
}
